// src/components/games/GlassBridge.tsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Coins, AlertTriangle } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

// CRD Configuration
const ENTRY_FEES = {
    MIN: 20,
    MEDIUM: 30,
    MAX: 50
};
const BASE_ENTRY_FEE = 10;

const globalCss = `
  :root {
    --r-white: #ffeeee;
    --r-100: #ffbaba;
    --r-200: #ff7b7b;
    --r-300: #ff4d4d;
    --r-400: #d80000;
    --r-500: #7a0000;
    --r-800: #400000;
    --r-900: #1a0000;
    --r-bg: #0a0000;
  }

  body {
    background-color: var(--r-bg);
    font-family: "Impact", "Arial Black", "Segoe UI Black", sans-serif;
    color: var(--r-white);
    letter-spacing: -0.5px;
  }

  .game-title {
    font-size: clamp(3rem, 8vw, 6rem);
    letter-spacing: -2px;
    text-transform: uppercase;
    text-align: center;
    line-height: 1;
    text-shadow: 
      -3px 3px 0px var(--r-500),
      -6px 6px 15px rgba(0,0,0,0.8);
    background: linear-gradient(to bottom, var(--r-100) 0%, var(--r-400) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 25px rgba(216, 0, 0, 0.4));
    margin-bottom: 1rem;
  }

  .hud-panel {
    background: linear-gradient(to bottom, var(--r-800) 0%, var(--r-500) 100%);
    border-radius: 18px;
    padding: 1rem 1.5rem;
    position: relative;
    overflow: hidden;
    box-shadow: 
      inset -2px 12px 11px -5px var(--r-400), 
      inset 1px -3px 11px 0px rgb(0 0 0 / 60%), 
      0 15px 30px rgba(0,0,0,0.8);
    border: 2px solid var(--r-500);
  }
  .hud-panel::before {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(to bottom, transparent, var(--r-400));
    opacity: 0.3; pointer-events: none;
  }
  .hud-label {
    color: var(--r-200);
    text-shadow: -1px 1px 2px var(--r-900);
    font-size: 0.85rem;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .hud-value {
    color: var(--r-white);
    font-size: 2.5rem;
    text-shadow: -2px 2px 4px var(--r-900), 0 0 15px rgba(255, 77, 77, 0.4);
    line-height: 1.1;
  }

  .abyss-bg {
    background: radial-gradient(ellipse at top, var(--r-800) 0%, var(--r-bg) 60%);
  }
  .abyss-grid {
    background-image: 
      linear-gradient(to right, rgba(216, 0, 0, 0.1) 2px, transparent 2px), 
      linear-gradient(to bottom, rgba(216, 0, 0, 0.1) 2px, transparent 2px);
    background-size: 5rem 5rem;
  }

  .glass-panel {
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    position: relative;
    font-size: 1.2rem;
    font-weight: 900;
    font-family: monospace;
  }
  
  .panel-future {
    background: rgba(122, 0, 0, 0.4);
    border: 2px solid rgba(216, 0, 0, 0.5);
    box-shadow: inset 0 0 20px rgba(0,0,0,0.6), 0 5px 15px rgba(0,0,0,0.5);
    color: rgba(255, 186, 186, 0.9);
    text-shadow: -1px 1px 2px var(--r-900);
  }

  .panel-active {
    background: linear-gradient(to bottom, rgba(255, 123, 123, 0.3) 0%, rgba(216, 0, 0, 0.6) 100%);
    border: 2px solid var(--r-200);
    box-shadow: 
      inset 0 0 20px rgba(255, 123, 123, 0.4), 
      0 8px 20px rgba(216, 0, 0, 0.4);
    color: var(--r-white);
    text-shadow: -2px 2px 4px var(--r-900), 0 0 15px rgba(255, 77, 77, 0.9);
    cursor: pointer;
  }
  .panel-active:hover {
    background: linear-gradient(to bottom, var(--r-300) 0%, var(--r-400) 100%);
    border-color: var(--r-100);
    color: var(--r-white);
    transform: translateZ(25px) scale(1.05);
    text-shadow: -1px 1px 3px var(--r-900);
    box-shadow: 
      inset -2px 12px 11px -5px var(--r-100), 
      inset 1px -3px 11px 0px rgb(0 0 0 / 50%),
      0 15px 30px rgba(216, 0, 0, 0.5);
  }

  .panel-safe {
    background: linear-gradient(to bottom, var(--r-100) 0%, var(--r-200) 100%);
    border: 4px solid var(--r-white);
    color: var(--r-900);
    text-shadow: 1px 1px 0px rgba(255,255,255,0.5);
    box-shadow: 
      inset -2px 12px 11px -5px var(--r-white), 
      inset 1px -3px 11px 0px rgba(122, 0, 0, 0.5),
      0 0 50px rgba(255, 186, 186, 0.6);
  }

  .panel-broken {
    background: rgba(216, 0, 0, 0.1);
    border: 1px solid rgba(122, 0, 0, 0.2);
    color: transparent;
    box-shadow: none;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px) rotate(1deg); }
    50% { transform: translateX(5px) rotate(-1deg); }
    75% { transform: translateX(-5px) rotate(1deg); }
  }
`;

const NUM_STEPS = 10;
const ROW_HEIGHT = 160; 
const BASE_Y_OFFSET = -600;

// 🟢 ArcText Component - For curved text
const ArcText = ({ text, className, style, color }: any) => {
  const letters = text.split('');
  const mid = (letters.length - 1) / 2;
  const curveAmount = 0.25;

  return (
    <div 
      className={className} 
      style={{ 
        ...style, 
        whiteSpace: 'nowrap', 
        display: 'block',
        background: 'none', 
        WebkitTextFillColor: 'initial' 
      }}
    >
      {letters.map((char: string, i: number) => {
        const dist = i - mid;
        const maxDist = mid || 1;
        const normalizedDist = dist / maxDist;
        const scaleY = 1 - curveAmount + (curveAmount * (normalizedDist * normalizedDist));
        
        return (
          <span 
            key={i} 
            style={{ 
              display: 'inline-block',
              transformOrigin: 'top center',
              transform: `scaleY(${scaleY})`,
              background: color ? 'none' : 'linear-gradient(to bottom, var(--r-100) 0%, var(--r-400) 100%)',
              WebkitBackgroundClip: color ? 'initial' : 'text',
              WebkitTextFillColor: color ? color : 'transparent',
              color: color || 'inherit'
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </div>
  );
};

// 🟢 RedAnimatedButton Component
const RedAnimatedButton = ({ onClick, text1, text2 }: any) => {
  return (
    <div className="relative inline-flex items-center justify-center my-6 scale-[0.8] sm:scale-100">
      <style>{`
        .custom-red-btn {
          --radius: 18px;
          border-radius: var(--radius);
          outline: none;
          cursor: pointer;
          font-size: 26px;
          font-family: inherit;
          background: transparent;
          letter-spacing: inherit;
          border: 0;
          position: relative;
          width: 250px;
          height: 90px;
          transform: rotate(353deg) skewX(4deg);
        }
        .custom-red-btn .bg { position: absolute; inset: 0; border-radius: inherit; filter: blur(1px); }
        .custom-red-btn .bg::before, .custom-red-btn .bg::after { content: ""; position: absolute; inset: 0; border-radius: calc(var(--radius) * 1.1); background: var(--r-500); }
        .custom-red-btn .bg::before {
          filter: blur(5px); transition: all 0.3s ease;
          box-shadow: -7px 6px 0 0 rgb(64 0 0 / 60%), -14px 12px 0 0 rgb(64 0 0 / 50%), -21px 18px 4px 0 rgb(64 0 0 / 40%), -28px 24px 8px 0 rgb(64 0 0 / 30%), -35px 30px 12px 0 rgb(64 0 0 / 20%);
        }
        .custom-red-btn .wrap {
          border-radius: inherit; overflow: hidden; height: 100%; transform: translate(6px, -6px); padding: 4px;
          background: linear-gradient(to bottom, var(--r-100) 0%, var(--r-400) 100%);
          position: relative; transition: all 0.3s ease;
        }
        .custom-red-btn .outline { position: absolute; overflow: hidden; inset: 0; opacity: 0; outline: none; border-radius: inherit; transition: all 0.4s ease; }
        .custom-red-btn .outline::before { content: ""; position: absolute; inset: 2px; width: 120px; height: 300px; margin: auto; background: linear-gradient(to right, transparent 0%, white 50%, transparent 100%); animation: spin 3s linear infinite; animation-play-state: paused; }
        .custom-red-btn .content {
          pointer-events: none; display: flex; align-items: center; justify-content: center; z-index: 1; position: relative; height: 100%; gap: 16px; border-radius: calc(var(--radius) * 0.85);
          background: linear-gradient(to bottom, var(--r-300) 0%, var(--r-400) 100%);
          box-shadow: inset -2px 12px 11px -5px var(--r-200), inset 1px -3px 11px 0px rgb(0 0 0 / 50%);
        }
        .custom-red-btn .content::before { content: ""; inset: 0; position: absolute; z-index: 10; width: 80%; top: 45%; bottom: 35%; opacity: 0.7; margin: auto; background: linear-gradient(to bottom, transparent, var(--r-400)); filter: brightness(1.3) blur(5px); }
        .custom-red-btn .char { transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; }
        .custom-red-btn .char span { display: block; color: transparent; position: relative; }
        .custom-red-btn .char.state-1 span { animation: charAppear 1.2s ease backwards calc(var(--i) * 0.03s); }
        .custom-red-btn .char.state-1 span::before, .custom-red-btn .char span::after { content: attr(data-label); position: absolute; color: var(--r-white); text-shadow: -1px 1px 3px var(--r-900); left: 0; }
        .custom-red-btn .char span::before { opacity: 0; transform: translateY(-100%); }
        .custom-red-btn .char.state-2 { position: absolute; left: 80px; }
        .custom-red-btn .char.state-2 span::after { opacity: 1; }
        .custom-red-btn .icon { animation: resetArrow 0.8s cubic-bezier(0.7, -0.5, 0.3, 1.2) forwards; z-index: 10; }
        .custom-red-btn .icon div, .custom-red-btn .icon div::before, .custom-red-btn .icon div::after { height: 4px; border-radius: 2px; background-color: var(--r-white); }
        .custom-red-btn .icon div::before, .custom-red-btn .icon div::after { content: ""; position: absolute; right: 0; transform-origin: center right; width: 16px; transition: all 0.3s ease; }
        .custom-red-btn .icon div { position: relative; width: 28px; box-shadow: -2px 2px 5px var(--r-900); transform: scale(0.9); background: linear-gradient(to bottom, var(--r-white), var(--r-100)); animation: swingArrow 1s ease-in-out infinite; animation-play-state: paused; }
        .custom-red-btn .icon div::before { transform: rotate(44deg); top: 1px; box-shadow: 1px -2px 3px -1px var(--r-900); animation: rotateArrowLine 1s linear infinite; animation-play-state: paused; }
        .custom-red-btn .icon div::after { bottom: 1px; transform: rotate(316deg); box-shadow: -2px 2px 3px 0 var(--r-900); background: linear-gradient(200deg, var(--r-white), var(--r-100)); animation: rotateArrowLine2 1s linear infinite; animation-play-state: paused; }
        .custom-red-btn .path { position: absolute; z-index: 12; bottom: 0; left: 0; right: 0; stroke-dasharray: 150 480; stroke-dashoffset: 150; pointer-events: none; }
        .custom-red-btn .splash { position: absolute; top: 0; left: 0; pointer-events: none; stroke-dasharray: 60 60; stroke-dashoffset: 60; transform: translate(-17%, -31%); stroke: var(--r-300); }
        
        .custom-red-btn:hover .words { opacity: 1; }
        .custom-red-btn:hover .words span { animation-play-state: running; }
        .custom-red-btn:hover .char.state-1 span::before { animation: charAppear 0.7s ease calc(var(--i) * 0.03s); }
        .custom-red-btn:hover .char.state-1 span::after { opacity: 1; animation: charDisappear 0.7s ease calc(var(--i) * 0.03s); }
        .custom-red-btn:hover .wrap { transform: translate(8px, -8px); }
        .custom-red-btn:hover .outline { opacity: 1; }
        .custom-red-btn:hover .outline::before, .custom-red-btn:hover .icon div::before, .custom-red-btn:hover .icon div::after, .custom-red-btn:hover .icon div { animation-play-state: running; }
        
        .custom-red-btn:active .bg::before { filter: blur(5px); opacity: 0.7; box-shadow: -7px 6px 0 0 rgb(64 0 0 / 60%), -14px 12px 0 0 rgb(64 0 0 / 40%), -21px 18px 4px 0 rgb(64 0 0 / 20%); }
        .custom-red-btn:active .content { box-shadow: inset -1px 12px 8px -5px rgba(64, 0, 0, 0.7), inset 0px -3px 8px 0px var(--r-200); }
        .custom-red-btn:active .words, .custom-red-btn:active .outline { opacity: 0; }
        .custom-red-btn:active .wrap { transform: translate(3px, -3px); }
        .custom-red-btn:active .splash { animation: splash 0.8s cubic-bezier(0.3, 0, 0, 1) forwards 0.05s; }
        .custom-red-btn:focus .path { animation: path 1.6s ease forwards 0.2s; }
        .custom-red-btn:focus .icon { animation: arrow 1s cubic-bezier(0.7, -0.5, 0.3, 1.5) forwards; }
        
        .custom-red-btn .char.state-2 span::after, .custom-red-btn:focus .char.state-1 span { animation: charDisappear 0.5s ease forwards calc(var(--i) * 0.03s); }
        .custom-red-btn:focus .char.state-2 span::after { animation: charAppear 1s ease backwards calc(var(--i) * 0.03s); }

        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes charAppear { 0% { transform: translateY(50%); opacity: 0; filter: blur(20px); } 20% { transform: translateY(70%); opacity: 1; } 50% { transform: translateY(-15%); opacity: 1; filter: blur(0); } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes charDisappear { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-70%); opacity: 0; filter: blur(3px); } }
        @keyframes arrow { 0% { opacity: 1; } 50% { transform: translateX(60px); opacity: 0; } 51% { transform: translateX(-200px); opacity: 0; } 100% { transform: translateX(-128px); opacity: 1; } }
        @keyframes swingArrow { 50% { transform: translateX(5px) scale(0.9); } }
        @keyframes rotateArrowLine { 50% { transform: rotate(30deg); } 80% { transform: rotate(55deg); } }
        @keyframes rotateArrowLine2 { 50% { transform: rotate(330deg); } 80% { transform: rotate(300deg); } }
        @keyframes resetArrow { 0% { transform: translateX(-128px); } 100% { transform: translateX(0); } }
        @keyframes path { from { stroke: white; } to { stroke-dashoffset: -480; stroke: var(--r-100); } }
        @keyframes splash { to { stroke-dasharray: 2 60; stroke-dashoffset: -60; } }
      `}</style>

      <button className="custom-red-btn" onClick={onClick}>
        <div className="bg"></div>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 342 208" height="208" width="342" className="splash">
          <path strokeLinecap="round" strokeWidth="3" d="M54.1054 99.7837C54.1054 99.7837 40.0984 90.7874 26.6893 97.6362C13.2802 104.485 1.5 97.6362 1.5 97.6362"></path>
          <path strokeLinecap="round" strokeWidth="3" d="M285.273 99.7841C285.273 99.7841 299.28 90.7879 312.689 97.6367C326.098 104.486 340.105 95.4893 340.105 95.4893"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M281.133 64.9917C281.133 64.9917 287.96 49.8089 302.934 48.2295C317.908 46.6501 319.712 36.5272 319.712 36.5272"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M281.133 138.984C281.133 138.984 287.96 154.167 302.934 155.746C317.908 157.326 319.712 167.449 319.712 167.449"></path>
          <path strokeLinecap="round" strokeWidth="3" d="M230.578 57.4476C230.578 57.4476 225.785 41.5051 236.061 30.4998C246.337 19.4945 244.686 12.9998 244.686 12.9998"></path>
          <path strokeLinecap="round" strokeWidth="3" d="M230.578 150.528C230.578 150.528 225.785 166.471 236.061 177.476C246.337 188.481 244.686 194.976 244.686 194.976"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M170.392 57.0278C170.392 57.0278 173.89 42.1322 169.571 29.54C165.252 16.9478 168.751 2.05227 168.751 2.05227"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M170.392 150.948C170.392 150.948 173.89 165.844 169.571 178.436C165.252 191.028 168.751 205.924 168.751 205.924"></path>
          <path strokeLinecap="round" strokeWidth="3" d="M112.609 57.4476C112.609 57.4476 117.401 41.5051 107.125 30.4998C96.8492 19.4945 98.5 12.9998 98.5 12.9998"></path>
          <path strokeLinecap="round" strokeWidth="3" d="M112.609 150.528C112.609 150.528 117.401 166.471 107.125 177.476C96.8492 188.481 98.5 194.976 98.5 194.976"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M62.2941 64.9917C62.2941 64.9917 55.4671 49.8089 40.4932 48.2295C25.5194 46.6501 23.7159 36.5272 23.7159 36.5272"></path>
          <path strokeLinecap="round" strokeWidth="3" strokeOpacity="0.3" d="M62.2941 145.984C62.2941 145.984 55.4671 161.167 40.4932 162.746C25.5194 164.326 23.7159 174.449 23.7159 174.449"></path>
        </svg>

        <div className="wrap">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 221 42" height="42" width="221" className="path">
            <path strokeLinecap="round" strokeWidth="3" d="M182.674 2H203C211.837 2 219 9.16344 219 18V24C219 32.8366 211.837 40 203 40H18C9.16345 40 2 32.8366 2 24V18C2 9.16344 9.16344 2 18 2H47.8855"></path>
          </svg>

          <div className="outline"></div>
          <div className="content">
            <span className="char state-1">
              {text1.split('').map((char: string, index: number) => (
                <span key={`1-${index}`} data-label={char === ' ' ? '' : char} style={{ '--i': index + 1 } as any}>
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </span>

            <div className="icon">
              <div></div>
            </div>

            <span className="char state-2">
              {text2.split('').map((char: string, index: number) => (
                <span key={`2-${index}`} data-label={char === ' ' ? '' : char} style={{ '--i': index + 1 } as any}>
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};

const generateBridge = () => {
  return Array.from({ length: NUM_STEPS }).map((_, i) => ({
    leftPrize: Math.floor(Math.random() * 90 + 10) * (i + 1),
    rightPrize: Math.floor(Math.random() * 90 + 10) * (i + 1),
    safeSide: Math.random() > 0.5 ? 'left' : 'right',
  }));
};

// 🟢 Glass Panel Component
const GlassPanel = ({ side, row, stepIndex, data, brokenPanel, onClick }: any) => {
  const isActive = row === stepIndex;
  const isPast = row < stepIndex;
  const isBrokenThis = brokenPanel?.row === row && brokenPanel?.side === side;
  const prize = side === 'left' ? data.leftPrize : data.rightPrize;

  let stateClass = "panel-future";
  let transformStyle: any = {};

  if (isBrokenThis) {
    stateClass = "panel-broken opacity-0";
    transformStyle = { 
      transform: 'translateZ(-1500px) rotateX(720deg) rotateY(360deg)', 
      transitionDuration: '1.2s', 
      transitionTimingFunction: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)' 
    };
  } else if (isPast) {
    stateClass = "panel-safe";
  } else if (isActive) {
    stateClass = "panel-active";
  }

  return (
    <div 
      onClick={() => isActive && onClick(row, side)}
      className={`glass-panel w-32 sm:w-44 h-32 sm:h-44 ${stateClass}`}
      style={{ transformStyle: 'preserve-3d', ...transformStyle }}
    >
      <span className={`transition-opacity duration-300 ${(isPast || isActive) ? 'opacity-100' : 'opacity-80'} text-xs sm:text-sm`}>
        {prize} CRD
      </span>
    </div>
  );
};

interface GlassBridgeProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  updateProfile: (data: any) => Promise<void>;
  onBuyCredits: () => void;
}

export const GlassBridge: React.FC<GlassBridgeProps> = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  updateProfile,
  onBuyCredits 
}) => {
  const [bridgeData, setBridgeData] = useState<any[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [winnings, setWinnings] = useState(0);
  const [gameState, setGameState] = useState('menu'); 
  const [brokenPanel, setBrokenPanel] = useState<any>(null);
  const [camConfig, setCamConfig] = useState({ y: 0, z: -400, rotateX: 65 });
  const [isMobile, setIsMobile] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [isScreenShaking, setIsScreenShaking] = useState(false);
  const [entryFee, setEntryFee] = useState(ENTRY_FEES.MIN);
  const [balance, setBalance] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (currentUser?.cred !== undefined) {
      setBalance(currentUser.cred);
    }
  }, [currentUser]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (gameState === 'playing') {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setGameState('falling');
            setTimeout(() => setGameState('gameover'), 1500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState]);

  useEffect(() => {
    setIsScreenShaking(timeLeft <= 3 && gameState === 'playing');
  }, [timeLeft, gameState]);

  useEffect(() => {
    const baseZ = isMobile ? -300 : -100;
    
    if (gameState === 'playing') {
      const creepZ = (10 - timeLeft) * 15;
      setCamConfig({ y: stepIndex * ROW_HEIGHT + BASE_Y_OFFSET, z: baseZ + creepZ, rotateX: 60 });
    } else if (gameState === 'ready') {
      setCamConfig({ y: BASE_Y_OFFSET - 450, z: baseZ - 50, rotateX: 60 });
    } else if (gameState === 'falling') {
      setCamConfig({ y: stepIndex * ROW_HEIGHT + BASE_Y_OFFSET - 200, z: 600, rotateX: 85 });
    } else if (gameState === 'gameover') {
      setCamConfig({ y: stepIndex * ROW_HEIGHT + BASE_Y_OFFSET - 400, z: 1000, rotateX: 90 });
    } else if (gameState === 'won') {
      setCamConfig({ y: NUM_STEPS * ROW_HEIGHT + BASE_Y_OFFSET + 200, z: isMobile ? -500 : -300, rotateX: 45 });
    } else if (gameState === 'menu') {
      setCamConfig({ y: BASE_Y_OFFSET - ROW_HEIGHT, z: baseZ, rotateX: 60 });
    }
  }, [gameState, stepIndex, isMobile, timeLeft]);

  useEffect(() => {
    setBridgeData(generateBridge());
  }, []);

  if (!isOpen) return null;

  const startGame = () => {
    if (balance < entryFee) {
      setError(`Insufficient balance. Need ${entryFee} CRD to play.`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    const newBalance = balance - entryFee;
    setBalance(newBalance);
    updateProfile({ cred: newBalance });

    setBridgeData(generateBridge());
    setStepIndex(0);
    setWinnings(0);
    setBrokenPanel(null);
    setTimeLeft(10);
    setGameState('ready');
    setError(null);
  };

  const handleStep = async (row: number, side: string) => {
    if (gameState !== 'playing' || row !== stepIndex) return;
    const isSafe = bridgeData[row].safeSide === side;
    const prize = side === 'left' ? bridgeData[row].leftPrize : bridgeData[row].rightPrize;

    if (isSafe) {
      const newWinnings = winnings + prize;
      setWinnings(newWinnings);
      setTimeLeft(10);
      
      if (row === NUM_STEPS - 1) {
        setStepIndex(row + 1);
        setGameState('won');
        
        const finalBalance = balance + newWinnings;
        setBalance(finalBalance);
        await updateProfile({ cred: finalBalance });
        
        try {
          await addDoc(collection(db, "notifications"), {
            userId: currentUser.uid,
            type: 'reward',
            title: 'GLASS BRIDGE SURVIVED! 🏆',
            message: `You survived the Glass Bridge and won ${newWinnings} CRD!`,
            read: false,
            createdAt: serverTimestamp()
          });
        } catch (e) {
          console.error("Failed to save notification:", e);
        }
      } else {
        setStepIndex(row + 1);
      }
    } else {
      setBrokenPanel({ row, side });
      setGameState('falling');
      setTimeout(() => setGameState('gameover'), 1500);
    }
  };

  return (
    <>
      <style>{globalCss}</style>
      <div className="fixed inset-0 z-[999] w-full h-screen overflow-hidden select-none abyss-bg">
        
        {/* 🔥 TOP BAR - Back button left, Balance right - NO border line */}
        <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-3 sm:px-4 py-3">
          <button
            onClick={onClose}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-black/60 border border-red-500/30 rounded-xl text-white hover:bg-red-500/20 hover:border-red-400 transition-all duration-300 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">Back</span>
          </button>

          <div className="flex items-center gap-2 bg-black/60 border border-red-500/30 rounded-xl px-3 sm:px-4 py-2">
            <Coins size={16} className="text-yellow-400" />
            <span className="text-white font-bold font-mono text-xs sm:text-sm">{balance} CRD</span>
          </div>
        </div>

        {/* 🔥 Error Toast */}
        {error && (
          <div className="absolute top-16 sm:top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500/90 backdrop-blur-md text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl border border-red-400 shadow-[0_0_30px_rgba(216,0,0,0.5)] animate-in slide-in-from-top-8 fade-in duration-300 text-xs sm:text-sm">
            <AlertTriangle size={16} className="inline mr-2" />
            {error}
          </div>
        )}

        <div style={{ perspective: '1200px' }} className="w-full h-full flex items-center justify-center">
          <div 
            className="flex flex-col-reverse gap-8 sm:gap-12 items-center transition-transform duration-[1200ms] ease-[cubic-bezier(0.25,0.8,0.25,1)]"
            style={{
              transform: `rotateX(${camConfig.rotateX}deg) translateY(${camConfig.y}px) translateZ(${camConfig.z}px)`,
              transformStyle: 'preserve-3d'
            }}
          >
            {/* The Abyss Grid (Floor) - BACKGROUND */}
            <div 
              className="absolute inset-[-200%] pointer-events-none abyss-grid opacity-30"
              style={{ transform: 'translateZ(-800px)' }}
            />

            {/* Start Platform */}
            <div 
              onClick={() => {
                if (gameState === 'ready') {
                  setGameState('playing');
                  setTimeLeft(10);
                }
              }}
              className={`hud-panel w-80 sm:w-[450px] mb-20 flex flex-col items-center justify-center transition-all duration-500 z-10 ${
                gameState === 'ready' 
                  ? 'cursor-pointer hover:scale-105 shadow-[0_0_50px_rgba(216,0,0,0.6)] animate-pulse' 
                  : ''
              }`}
              style={{ minHeight: '160px' }}
            >
              <span className="game-title text-5xl sm:text-7xl text-center" style={{ margin: 0, filter: 'none', lineHeight: '1.1' }}>
                START
              </span>
            </div>

            {/* Bridge Rows */}
            {bridgeData.map((data: any, idx: number) => (
              <div key={idx} className="flex gap-8 sm:gap-16" style={{ transformStyle: 'preserve-3d' }}>
                <GlassPanel side="left" row={idx} data={data} stepIndex={stepIndex} brokenPanel={brokenPanel} onClick={handleStep} />
                <GlassPanel side="right" row={idx} data={data} stepIndex={stepIndex} brokenPanel={brokenPanel} onClick={handleStep} />
              </div>
            ))}

            {/* End Platform */}
            <div 
              className="hud-panel w-80 sm:w-[450px] mt-16 flex items-center justify-center z-10"
              style={{ 
                transform: `rotateX(${-camConfig.rotateX}deg)`,
                transformOrigin: 'bottom',
                minHeight: '140px'
              }}
            >
              <span className="game-title text-5xl sm:text-6xl" style={{ margin: 0, filter: 'none' }}>FINISH</span>
            </div>
            
          </div>
        </div>

        {/* --- UI Overlay Layer --- */}
        <div className={`absolute inset-0 z-20 pointer-events-none flex flex-col justify-between p-3 sm:p-4 ${isScreenShaking ? 'animate-[shake_0.2s_ease-in-out_infinite]' : ''}`}>
          
          {/* Main Menu Screen */}
          {gameState === 'menu' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--r-bg)]/90 backdrop-blur-md pointer-events-auto transition-opacity duration-1000 z-50">
              <div 
                className="text-center flex flex-col items-center justify-center hud-panel"
                style={isMobile ? { width: '100%', height: '100%', borderRadius: 0, border: 'none', transform: 'none', padding: '1.5rem' } : { padding: '3rem', transform: 'scale(1.1)' }}
              >
                <div className="mb-2" style={{ filter: 'drop-shadow(0 0 40px var(--r-200))' }}>
                  <ArcText text="THE GLASS" className="game-title" style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', marginBottom: '-0.5rem' }} />
                  <ArcText text="BRIDGE" className="game-title" style={{ fontSize: 'clamp(3.5rem, 10vw, 6.5rem)' }} />
                </div>
                
                {/* Entry Fee Selector - 20, 30, 50 CRD */}
                <div className="flex flex-col items-center gap-3 mb-6 mt-2">
                  <p className="text-[10px] text-zinc-400 font-mono uppercase tracking-widest">Entry Fee + {BASE_ENTRY_FEE} CRD Base</p>
                  <div className="flex gap-3">
                    {Object.entries(ENTRY_FEES).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => setEntryFee(value)}
                        className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-black uppercase tracking-widest text-xs sm:text-sm transition-all duration-300 ${
                          entryFee === value
                            ? 'bg-white text-black scale-105 shadow-[0_0_30px_rgba(255,255,255,0.3)]'
                            : 'bg-black/60 border border-red-500/30 text-zinc-400 hover:text-white hover:border-red-500/60'
                        }`}
                      >
                        {value} CRD
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-sm sm:text-lg mt-2 mb-4 sm:mb-6 max-w-md mx-auto px-2" style={{ color: 'var(--r-200)' }}>
                  Every step holds CRD. One pane is flawed. Move fast before the timer runs out.
                </p>
                
                <div className="flex flex-col items-center gap-2">
                  <RedAnimatedButton onClick={startGame} text1="START GAME" text2="PLAY NOW" />
                  {balance < entryFee && (
                    <button
                      onClick={onBuyCredits}
                      className="text-[10px] sm:text-xs text-yellow-400 hover:text-yellow-300 underline underline-offset-2 font-bold uppercase tracking-widest"
                    >
                      Buy More CRD →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 🟢 HUD - Total Winnings & Time Left (Under the top bar) */}
          {(gameState === 'ready' || gameState === 'playing' || gameState === 'falling') && (
            <div className="flex justify-between items-start w-full pointer-events-auto mt-12 sm:mt-14">
              <div className="hud-panel transition-transform duration-300" style={{ padding: '0.5rem 1rem' }}>
                <h2 className="hud-label text-[8px] sm:text-[10px]">Total Winnings</h2>
                <p className="hud-value text-base sm:text-2xl">{winnings} CRD</p>
              </div>
              
              <div className={`hud-panel text-right transition-all duration-300 ${timeLeft <= 3 ? 'animate-pulse ring-4 ring-red-500 scale-105' : ''}`} style={{ padding: '0.5rem 1rem' }}>
                <h2 className="hud-label text-[8px] sm:text-[10px]">Time Left</h2>
                <p className={`hud-value text-base sm:text-2xl ${timeLeft <= 3 ? '!text-red-500' : ''}`}>
                  {timeLeft}s
                </p>
              </div>
            </div>
          )}

          {/* Game Over Screen */}
          {gameState === 'gameover' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--r-900)]/80 backdrop-blur-md pointer-events-auto transition-opacity duration-1000 z-50">
              <div className="text-center flex flex-col items-center px-4" style={{ transform: isMobile ? 'none' : 'scale(1.1)' }}>
                <ArcText text="ELIMINATED" className="game-title" color="var(--r-300)" style={{ fontSize: 'clamp(3rem, 8vw, 5rem)' }} />
                <p className="text-xl sm:text-2xl mb-4" style={{ color: 'var(--r-100)' }}>YOU LOST {winnings} CRD</p>
                <p className="text-xs sm:text-sm text-zinc-400 mb-6">Entry fee {entryFee} CRD deducted</p>
                <RedAnimatedButton onClick={startGame} text1="TRY AGAIN" text2="PLAY NOW" />
              </div>
            </div>
          )}

          {/* Victory Screen */}
          {gameState === 'won' && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--r-bg)]/90 backdrop-blur-xl pointer-events-auto transition-opacity duration-1000 z-50">
              <div 
                className="text-center flex flex-col items-center justify-center hud-panel px-4"
                style={isMobile ? { width: '100%', height: '100%', borderRadius: 0, border: 'none', transform: 'none', padding: '1.5rem' } : { padding: '3rem', transform: 'scale(1.1)' }}
              >
                <ArcText text="SURVIVED" className="game-title" style={{ fontSize: 'clamp(3rem, 10vw, 4rem)', filter: 'drop-shadow(0 0 40px var(--r-200))' }} />
                <p className="text-lg sm:text-xl mt-4 mb-2" style={{ color: 'var(--r-200)' }}>YOU SECURED</p>
                <p className="hud-value text-3xl sm:text-5xl mb-6" style={{ fontSize: 'clamp(3rem, 10vw, 4.5rem)' }}>{winnings} CRD</p>
                <p className="text-xs sm:text-sm text-green-400 mb-4">+{winnings} CRD added to your balance!</p>
                <RedAnimatedButton onClick={startGame} text1="PLAY AGAIN" text2="PLAY NOW" />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};